import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTweaks } from '../components/Tweaks.jsx';
import { useOffer, useOfferTweakDefaults } from '../offers/OfferContext.jsx';
import { OFFER_VARIANTS } from '../offers/offers.js';
import { getStoredSource } from '../attribution/source.js';
import { markHasAccount } from '../account/hasAccount.js';
import { getSessionId, fireEvent } from '../analytics.js';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import {
  PayHero, PayCheckoutForm, PayCheckoutLoading, PayNoCardStart, PayAlreadyHasAccess,
  PayTickStyleInjector, PAY_LANGS,
} from '../components/Pay.jsx';

const PRODUCT_IDS = {
  monthly: 'standard-month-15-gbp-trial7',
  annual:  'standard-year-150-gbp-trial7',
};

// Promo cohorts whose source tag buys them a non-default pay flow WITHOUT
// an offer-test variant stamp: no `v` reaches the backend, so they never
// touch the test's arms, and Ivan separates them by source alone. Reuses
// the test's variant definitions so flow + copy can't drift from what's
// already wired and verified against the backend products.
const PROMO_PAY_FLOWS = {
  // Adult Learners' Week (/alw, Nick) → v2's card-less free 30 days.
  'nick-alw': OFFER_VARIANTS.v2,
};

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',
  payKicker: 'Start your 7 days free',
  payH1Part1: "Let's get you speaking",
  payH1Empty: "Let's get you speaking.",
  payLede: "Three quick choices, then you're in. No card charged for seven days – cancel any time before then and you'll never see a penny leave your account.",
  payStep1Title: 'Pick your language',
  payStep1Sub: "Your subscription unlocks all of them – this is just where you'll start.",
  payStep1Note: 'You can switch to a different language any time from inside the app.',
  payStep2Title: 'Choose a plan',
  payStep2Sub: 'Both come with a full 7-day free trial. Switch between them whenever you like.',
  payPlanMonthlyName: 'Monthly',
  payPlanMonthlyBlurb: 'The flexible option. Cancel any time, no questions.',
  payPlanAnnualName: 'Annual',
  payPlanAnnualBlurb: 'The committed option. Two months free when you go yearly.',
  payPlanAnnualSave: 'Save £30',
  payStep3Title: 'Create your account',
  payStep3Sub: "Your login for the app – we'll only ever use your email to send you things you actually asked for.",
  payEmailLabel: 'Email',
  payEmailPlaceholder: 'you@example.com',
  payHasAccessTitle: 'You already have access',
  payHasAccessMsg: "This email is linked to an account that already has full access to the app — there's no need to start a new subscription.",
  payHasAccessCta: 'Open the app',
  payStep4Title: 'Card details',
  payStep4Sub: "Securely handled by Stripe. Nothing is charged today – we only take payment on day 8 if you haven't cancelled.",
  payStripeCredit: 'Powered by Stripe · your card details never touch our servers',
  payCta: 'Start my 7 days free',
  payCtaEmpty: 'Pick a language to continue',
  payCtaFine: "By starting your trial you agree to our Terms and Privacy Policy. No card is charged today; we'll only bill you on day 8 if you haven't cancelled.",
  paySummaryKicker: 'What happens next',
  paySummaryFor: 'Your trial of',
  paySummaryNoLang: 'Your 7-day trial',
  payTl1Title: 'Today',
  payTl1Body: 'Instant access to all 11 courses. Start your first lesson the moment you press the button.',
  payTl2Title: "Day 6 – we'll email you",
  payTl2Body: "A friendly nudge that your trial is ending in 48 hours. Cancel in one click if it's not for you.",
  payTl3TitleM: 'Day 8 – first charge',
  payTl3BodyM: 'Billed monthly on the same date from then on. Cancel any time and billing stops at the end of the month.',
  payTl3TitleA: 'Day 8 – first charge',
  payTl3BodyA: 'One payment for 12 months. Price locked in for life as long as your subscription stays active.',
  payTl3PriceM: '£15',
  payTl3PriceA: '£150',
  payIncludesTitle: "What's included",
  payIncl1: "Every course we've built – 11 languages and counting",
  payIncl2: 'High-intensity spoken lessons, the SaySomethingin Method',
  payIncl3: 'Full community access – forum, events and practice sessions',
  payIncl4: 'Progress saved across phone, tablet and web',
  payQuote: "\"It's absolutely brilliant, it's really good fun, it's really easy to use – I don't know how it works but it does.\"",
  payQuoteAttr: '— Ruth Jones, actor & writer (Gavin & Stacey)',
  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Pay – hero & steps', keys: ['payKicker','payH1Part1','payH1Empty','payLede','payStep1Title','payStep1Sub','payStep1Note','payStep2Title','payStep2Sub','payPlanMonthlyName','payPlanMonthlyBlurb','payPlanAnnualName','payPlanAnnualBlurb','payPlanAnnualSave','payStep3Title','payStep3Sub','payEmailLabel','payEmailPlaceholder','payHasAccessTitle','payHasAccessMsg','payHasAccessCta','payStep4Title','payStep4Sub','payStripeCredit','payCta','payCtaEmpty','payCtaFine'], long:['payLede','payStep1Sub','payStep1Note','payStep2Sub','payStep3Sub','payHasAccessMsg','payStep4Sub','payStripeCredit','payCtaFine'] },
  { name: 'Pay – summary panel', keys: ['paySummaryKicker','paySummaryFor','paySummaryNoLang','payTl1Title','payTl1Body','payTl2Title','payTl2Body','payTl3TitleM','payTl3BodyM','payTl3TitleA','payTl3BodyA','payIncludesTitle','payIncl1','payIncl2','payIncl3','payIncl4','payQuote','payQuoteAttr'], long:['payTl1Body','payTl2Body','payTl3BodyM','payTl3BodyA','payQuote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Pay() {
  const offer = useOffer();
  const [searchParams, setSearchParams] = useSearchParams();

  // Promo flow (e.g. /alw cohort): active only when no test variant is
  // assigned, so the offer test always wins if both somehow apply.
  const promo = !offer
    ? (PROMO_PAY_FLOWS[searchParams.get('s') ?? getStoredSource() ?? ''] ?? null)
    : null;

  const offerPay = offer ? offer.config.pay : promo ? promo.pay : null;
  // Variant flags – control behaves exactly as before.
  const showPlans = offerPay ? offerPay.showPlans : true;
  const showCard  = offerPay ? offerPay.showCard  : true;
  const productIds = (offerPay ? offerPay.productIds : null) ?? PRODUCT_IDS;

  const offerDefaults = useOfferTweakDefaults(TWEAK_DEFAULTS, 'pay');
  // Promo cohorts get the flow's pay-page copy the same way a variant would.
  const defaults = promo
    ? { ...offerDefaults, ...(promo.tweaks.shared || {}), ...(promo.tweaks.pay || {}) }
    : offerDefaults;
  const { tweaks, panel } = useTweaks({ defaults, groups: GROUPS });

  const initial = useMemo(() => {
    const course = searchParams.get('course');
    const planRaw = searchParams.get('plan');
    const fallbackPlan = offerPay ? offerPay.defaultPlan : 'annual';
    return {
      courseIdx: course ? Math.max(-1, PAY_LANGS.findIndex(l => l.code === course)) : -1,
      // Without a visible plan step there is no plan choice to honour.
      plan: !showPlans ? fallbackPlan
        : planRaw === 'monthly' ? 'monthly'
        : planRaw === 'annual'  ? 'annual'
        : fallbackPlan,
      // Explicit ?s= wins; otherwise fall back to a source stored by a
      // promo landing page (e.g. /alw) so tagged cohorts stay tagged even
      // when they arrive here via an untagged nav CTA.
      source: searchParams.get('s') ?? getStoredSource(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceRef      = useRef(initial.source);
  const checkAbortRef  = useRef(null);

  const [courseIdx, setCourseIdx]           = useState(initial.courseIdx);
  const [plan, setPlan]                     = useState(initial.plan);
  const [email, setEmail]                   = useState('');
  const [emailStatus, setEmailStatus]       = useState('idle'); // 'idle'|'checking'|'has_access'|'clear'
  const [clientSecret, setClientSecret]     = useState(null);
  const [fetchingSecret, setFetchingSecret] = useState(false);
  const [sessionError, setSessionError]     = useState(null);
  const [retryCount, setRetryCount]         = useState(0);

  const onEmailChange = useCallback(val => {
    setEmail(val);
    setEmailStatus('idle');
  }, []);

  const onEmailBlur = useCallback(() => {
    const val = email.trim();
    if (!val.includes('@') || !val.includes('.')) return;
    if (checkAbortRef.current) checkAbortRef.current.abort();
    const controller = new AbortController();
    checkAbortRef.current = controller;
    setEmailStatus('checking');
    const url = `${import.meta.env.VITE_API_BASE}/membership/users/check-access?${new URLSearchParams({ email: val })}`;
    fetch(url, { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(({ has_access }) => {
        setEmailStatus(has_access ? 'has_access' : 'clear');
        if (has_access) markHasAccount();
      })
      .catch(err => { if (err.name !== 'AbortError') setEmailStatus('clear'); });
  }, [email]);

  // Fire a one-shot landing-page-visit beacon on mount.
  useEffect(() => {
    const data = {};
    if (offer) data.variant = offer.variant;
    if (sourceRef.current) data.source = sourceRef.current;
    if (initial.courseIdx >= 0) data.campaign = PAY_LANGS[initial.courseIdx].code;
    fireEvent('landing-page-visit', data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync so reloads / back-button preserve the choice.
  useEffect(() => {
    const next = new URLSearchParams();
    if (courseIdx >= 0) next.set('course', PAY_LANGS[courseIdx].code);
    if (showPlans) next.set('plan', plan);
    if (sourceRef.current) next.set('s', sourceRef.current);
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIdx, plan]);

  // Fetch a Checkout Session client secret whenever language or plan changes.
  // courseIdx < 0 means no language selected yet — nothing to fetch.
  // Card-less variants (offer v2) never touch Stripe.
  useEffect(() => {
    if (courseIdx < 0 || !showCard) {
      setClientSecret(null);
      setSessionError(null);
      return;
    }

    const apiBase = import.meta.env.VITE_API_BASE;
    if (!apiBase) {
      setSessionError('VITE_API_BASE is not configured.');
      return;
    }

    const productId = productIds[plan];
    const url = `${apiBase}/membership/onestep/${productId}`;

    let cancelled = false;
    setFetchingSecret(true);
    setClientSecret(null);
    setSessionError(null);

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        elements: true,
        campaign: PAY_LANGS[courseIdx].code,
        ...(sourceRef.current ? { source: sourceRef.current } : {}),
        ...(offer ? { variant: offer.variant } : {}),
        session_id: getSessionId(),
      }),
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(({ secret }) => {
        if (!cancelled) { setClientSecret(secret); setFetchingSecret(false); }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionError('Could not initialise payment — please try again.');
          setFetchingSecret(false);
        }
      });

    return () => { cancelled = true; };
  }, [courseIdx, plan, retryCount]);

  const cardStepNum = showPlans ? 4 : 3;
  const checkoutSlot = emailStatus === 'has_access' ? (
    <PayAlreadyHasAccess t={tweaks}/>
  ) : !showCard ? (
    <PayNoCardStart
      t={tweaks}
      courseIdx={courseIdx}
      email={email}
      productId={productIds[plan]}
      variant={offer ? offer.variant : null}
      source={sourceRef.current}
    />
  ) : clientSecret ? (
    <PayCheckoutForm t={tweaks} courseIdx={courseIdx} clientSecret={clientSecret} email={email} stepNum={cardStepNum}/>
  ) : (
    <PayCheckoutLoading
      t={tweaks}
      courseIdx={courseIdx}
      loading={fetchingSecret}
      error={sessionError}
      onRetry={() => setRetryCount(n => n + 1)}
      stepNum={cardStepNum}
    />
  );

  return (
    <div data-screen-label="Pay">
      <PayTickStyleInjector/>
      <Header t={tweaks}/>
      <main>
        <PayHero
          t={tweaks}
          courseIdx={courseIdx} setCourseIdx={setCourseIdx}
          plan={plan} setPlan={setPlan}
          email={email} onEmailChange={onEmailChange}
          emailStatus={emailStatus} onEmailBlur={onEmailBlur}
          checkoutSlot={checkoutSlot}
          offerPay={offerPay}
        />
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
